import 'react-datepicker/dist/react-datepicker.css';
import './styles/global.scss';
import 'react-toastify/dist/ReactToastify.min.css';

import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import MaintenanceScreen from './atoms/MaintenanceScreen/MaintenanceScreen';
import UnsavedChangesPopup from './atoms/UnsavedChangesPopup/UnsavedChangesPopup';
import Layout from './components/Layout/Layout';
import ExperimentalSetting from './components/Settings/ExperimentalSetting/ExperimentalSetting';
import GeneralSetting from './components/Settings/GeneralSetting/GeneralSetting';
import LanguageSetting from './components/Settings/LanguageSetting/LanguageSetting';
import SettingsLayout from './components/Settings/SettingsLayout';
import TechSetting from './components/Settings/TechSetting/TechSetting';
import { getAccessToken } from './Functions/LocalStorageUtils';
import useNavigationBlocker from './Hooks/UseNavigationBlocker';
import { useCheckUserTokensMutation, useValidateSessionMutation } from './redux/api/auth';
import { useAppDispatch, useAppSelector } from './redux/store';

type PendingTransition = {
  location: {
    pathname: string;
  };
};

const App: React.FC = () => {
  const dispatch = useAppDispatch();
  const [checkUserToken] = useCheckUserTokensMutation();
  const [validateSession] = useValidateSessionMutation();
  const intervalRef = useRef<ReturnType<typeof setInterval>>();
  const [authChecked, setAuthChecked] = useState(false);
  const [pendingTransition, setPendingTransition] = useState<PendingTransition | null>(null);
  const [allowOneNavigation, setAllowOneNavigation] = useState(false);
  const [queuedNavigationPath, setQueuedNavigationPath] = useState<string | null>(null);
  const hasUnsavedChanges = useAppSelector((state) => state.app.hasUnsavedChanges);
  const maintenanceActive = useAppSelector((state) => state.auth.maintenance.active);
  const location = useLocation();
  const navigate = useNavigate();
  const shouldBlockNavigation = hasUnsavedChanges && location.pathname === '/' && !allowOneNavigation;

  const getToken = useCallback(() => {
    if(getAccessToken()){
      checkUserToken();
    }
  }, [checkUserToken]);

  useEffect(() => {
    const validate = async () => {
      await validateSession();
      getToken();
      setAuthChecked(true);
    };
    validate();
  }, [dispatch, getToken, validateSession]);

  useEffect(() => {
    const interval = setInterval(() => getToken(), 600000);
    intervalRef.current = interval;
    return () => clearInterval(interval);
  }, [getToken]);

  useNavigationBlocker((transition) => {
    setPendingTransition({
      location: {
        pathname: transition.location.pathname
      }
    });
  }, shouldBlockNavigation);

  useEffect(() => {
    if (allowOneNavigation && location.pathname !== '/') {
      setAllowOneNavigation(false);
    }
  }, [allowOneNavigation, location.pathname]);

  useEffect(() => {
    if (!allowOneNavigation || !queuedNavigationPath) {
      return;
    }

    navigate(queuedNavigationPath);
    setQueuedNavigationPath(null);
  }, [allowOneNavigation, navigate, queuedNavigationPath]);

  const handleLeaveWithUnsavedChanges = () => {
    if (!pendingTransition) {
      return;
    }

    const destinationPath = pendingTransition.location.pathname;
    setPendingTransition(null);
    setAllowOneNavigation(true);
    setQueuedNavigationPath(destinationPath);
  };

  const handleStayOnFloorplan = () => {
    setPendingTransition(null);
  };

  if (!authChecked) {
    return <div className="app" />;
  }

  if (maintenanceActive) {
    return (
      <div className="app">
        <MaintenanceScreen />
      </div>
    );
  }

  return (
    <div className="app">
      <ToastContainer
        position='bottom-right'
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{
          fontSize: '1.8rem',
          zIndex: '9999999999999999',
        }}
      />
      <Routes>
        <Route path="/" element={<Layout />} />
        <Route path="/settings" element={
            // protected route here
            <SettingsLayout />
          } 
        >
          <Route path="general" element={<GeneralSetting />} />
          <Route path="tech" element={<TechSetting />} />
          <Route path="experimental" element={<ExperimentalSetting />} />
          <Route path="language" element={<LanguageSetting />} />
          <Route
            index
            element={<Navigate to= '/settings/general' />}
          />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {pendingTransition && (
        <UnsavedChangesPopup
          onStay={handleStayOnFloorplan}
          onLeave={handleLeaveWithUnsavedChanges}
        />
      )}
    </div>
  );
};

export default App;
